"use client";

import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useHydrateServiceAccounts } from "@tillix/tx-core/react";
import { Button } from "@tillix/ui-atoms/button";
import { Form } from "@tillix/ui-atoms/form";
import { FormRootError } from "@tillix/ui-atoms/form-root-error";
import { untracked } from "mobx";
import { observer } from "mobx-react-lite";
import { FormProvider, useForm } from "react-hook-form";
import { useT } from "@/features/i18n/hooks/use-t";
import AccountField, { FIELD_CONFIGS } from "../fields/account-field";
import type {
  AddServiceAccountFormRenderProps,
  AddServiceAccountFormValues,
} from "./add-service-account.form-variants";

function AddServiceAccountForm({
  closeDialog,
  defaultValues,
  onAccountCreated,
  schema,
}: AddServiceAccountFormRenderProps) {
  const { t } = useT();
  const serviceAccountStore = useHydrateServiceAccounts();

  const form = useForm<AddServiceAccountFormValues>({
    resolver: arktypeResolver(schema),
    defaultValues: defaultValues,
    mode: "onChange",
  });

  const {
    formState: { isSubmitting, isValid, errors },
  } = form;
  const rootError = errors.root?.message;

  const handleSubmit = async (values: AddServiceAccountFormValues) => {
    const trimmed = values.nickname?.trim();
    if (trimmed) {
      const isDuplicate = untracked(() =>
        serviceAccountStore.data.some(
          (sa) =>
            sa.nickname != null &&
            sa.nickname.toLowerCase() === trimmed.toLowerCase(),
        ),
      );
      if (isDuplicate) {
        form.setError("nickname", {
          message: t("onboarding.duplicateNickname"),
        });
        return;
      }
    }

    try {
      const created = await serviceAccountStore.createServiceAccount({
        nickname: values.nickname?.trim() || "",
        externalId: values.externalId.trim(),
        meta: {
          zipCode: values.zipCode.trim(),
        },
      });
      onAccountCreated?.(created.id);
      form.reset(defaultValues);
      closeDialog();
    } catch (err) {
      console.error("failed to add service account", err);
      form.setError("root", {
        message:
          err instanceof Error ? err.message : t("accounts.couldNotAddAccount"),
      });
    }
  };

  return (
    <FormProvider {...form}>
      <Form
        className="flex flex-col gap-1"
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <AccountField<AddServiceAccountFormValues>
          {...FIELD_CONFIGS.externalId}
          name="externalId"
        />
        <AccountField<AddServiceAccountFormValues>
          {...FIELD_CONFIGS.zipCode}
          name="zipCode"
        />
        <FormRootError keyPrefix="add-sa" rootError={rootError} />
        <Button
          className="w-full"
          disabled={isSubmitting || !isValid}
          type="submit"
        >
          {isSubmitting ? t("accounts.adding") : t("accounts.addAccount")}
        </Button>
      </Form>
    </FormProvider>
  );
}

export default observer(AddServiceAccountForm);
